import { useId, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Botao } from '../componentes/Botao.js';
import {
  IconeAuditoria,
  IconeConfere,
  IconeDispositivos,
  IconeMarca,
  IconeMovimentacoes,
} from '../componentes/Icones.js';
import { TelaCarregando } from '../componentes/TelaCarregando.js';
import { ErroAutenticacao, entrar } from '../autenticacao/autenticacao-api.js';
import { useSessao } from '../autenticacao/SessaoContexto.js';
import './Entrar.css';

const MENSAGEM_GENERICA = 'Não foi possível entrar. Tente novamente.';

type EstadoDeOrigem = { readonly de?: string };

/**
 * O que o painel faz, dito no lado visual da tela de entrada.
 *
 * É texto de produto, não dado do armazém: nada aqui vem da API nem finge
 * ser número de operação. Cada frase corresponde a uma tela que existe.
 */
const DESTAQUES = [
  {
    Icone: IconeMovimentacoes,
    titulo: 'Operação por filial e depósito',
    texto:
      'Coleta, inventário e movimentação em cada depósito de cada filial, integradas ao Senior.',
  },
  {
    Icone: IconeDispositivos,
    titulo: 'Coletores offline-first',
    texto: 'O que foi registrado no chão de fábrica chega quando o aparelho volta a sincronizar.',
  },
  {
    Icone: IconeAuditoria,
    titulo: 'Trilha imutável',
    texto: 'Toda operação e toda decisão ficam registradas com autor e horário.',
  },
] as const;

/**
 * Entrada no painel (RF-005), em duas metades: o formulário à esquerda e, à
 * direita, o painel visual que diz o que a pessoa está prestes a operar.
 *
 * O formulário não guarda nada: a sessão vive no cookie HttpOnly que o
 * middleware devolve. Senha nunca vai para armazenamento local, nem em texto
 * nem em hash — some da memória quando a tela é desmontada.
 */
export function Entrar() {
  const { estado, registrarEntrada } = useSessao();
  const localizacao = useLocation();
  const idUsuario = useId();
  const idSenha = useId();

  const [usuario, definirUsuario] = useState('');
  const [senha, definirSenha] = useState('');
  const [erro, definirErro] = useState<string | null>(null);
  const [enviando, definirEnviando] = useState(false);

  if (estado.situacao === 'carregando') {
    return <TelaCarregando mensagem="Verificando a sessão…" />;
  }

  if (estado.situacao === 'autenticado') {
    const origem = (localizacao.state as EstadoDeOrigem | null)?.de;
    return <Navigate to={origem ?? '/visao-geral'} replace />;
  }

  function aoEnviar() {
    definirErro(null);
    definirEnviando(true);

    entrar({ usuario, senha })
      .then((sessao) => {
        definirSenha('');
        registrarEntrada(sessao);
      })
      .catch((causa: unknown) => {
        // Só a mensagem que o middleware mandou chega à tela; qualquer outra
        // falha vira texto genérico, para não expor detalhe de infraestrutura.
        definirErro(causa instanceof ErroAutenticacao ? causa.message : MENSAGEM_GENERICA);
      })
      .finally(() => {
        definirEnviando(false);
      });
  }

  return (
    <main className="entrar">
      <section className="entrar__formulario-lado">
        <div className="entrar__marca">
          <span className="entrar__marca-simbolo">
            <IconeMarca tamanho={20} />
          </span>
          <span className="entrar__marca-nome">Gestão de estoque</span>
        </div>

        <div className="entrar__caixa">
          <h1 className="entrar__titulo">Bem-vindo de volta</h1>
          <p className="entrar__subtitulo">Entre com o usuário do painel para continuar.</p>

          <form
            className="entrar__formulario"
            onSubmit={(evento) => {
              evento.preventDefault();
              aoEnviar();
            }}
          >
            <div className="entrar__campo">
              <label className="entrar__rotulo" htmlFor={idUsuario}>
                Usuário
              </label>
              <input
                id={idUsuario}
                className="entrar__entrada"
                name="usuario"
                type="text"
                autoComplete="username"
                autoFocus
                required
                value={usuario}
                onChange={(evento) => {
                  definirUsuario(evento.target.value);
                }}
              />
            </div>

            <div className="entrar__campo">
              <label className="entrar__rotulo" htmlFor={idSenha}>
                Senha
              </label>
              <input
                id={idSenha}
                className="entrar__entrada"
                name="senha"
                type="password"
                autoComplete="current-password"
                required
                value={senha}
                onChange={(evento) => {
                  definirSenha(evento.target.value);
                }}
              />
            </div>

            {erro !== null && (
              <p className="entrar__erro" role="alert">
                {erro}
              </p>
            )}

            <Botao type="submit" variante="primario" disabled={enviando}>
              {enviando ? 'Entrando…' : 'Entrar'}
            </Botao>
          </form>

          <p className="entrar__nota">
            Sem acesso? Peça ao administrador do seu armazém para cadastrar o seu usuário.
          </p>
        </div>

        <p className="entrar__rodape">Plataforma WMS · integrada ao Senior ERP</p>
      </section>

      <aside className="entrar__painel" aria-hidden="true">
        <div className="entrar__painel-brilho" />
        <div className="entrar__painel-grade" />
        <div className="entrar__painel-conteudo">
          <span className="entrar__painel-marca">
            <IconeMarca tamanho={26} />
          </span>
          <span className="entrar__painel-selo">
            <IconeConfere tamanho={13} />
            Painel de gestão
          </span>
          <h2 className="entrar__painel-titulo">
            A operação do armazém em cada filial e depósito, integrada ao Senior.
          </h2>
          <p className="entrar__painel-texto">
            O que acontece no chão de cada depósito chega ao ERP pela fila de integração, com quem
            coletou, contou ou moveu, quando e em qual aparelho.
          </p>

          <ul className="entrar__destaques">
            {DESTAQUES.map(({ Icone, titulo, texto }) => (
              <li key={titulo} className="entrar__destaque">
                <span className="entrar__destaque-icone">
                  <Icone tamanho={18} />
                </span>
                <span className="entrar__destaque-texto">
                  <span className="entrar__destaque-titulo">{titulo}</span>
                  <span className="entrar__destaque-detalhe">{texto}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </aside>
    </main>
  );
}
